<template>
  <div class="profession-matching-game">
    <LevelComplete v-if="gameOver" />

    <div class="profession-targets" v-if="currentProfession">
      <h2 class="profession-label">{{ currentProfession.name }}</h2>
      <div
        class="profession-target"
        :class="currentProfession.name.toLowerCase() + '-target'"
        @dragover.prevent
        @drop="handleDrop"
        @click="speakOutLoud(currentProfession.name)"
        ref="targetRef"
        :style="{ backgroundImage: 'url(' + currentProfession.backgroundImage + ')' }"
      >
        <div class="dropped-items">
          <span
            v-for="item in correctDrops[currentProfession.name]"
            :key="item.id"
          >{{ item.emoji }}</span>
        </div>
      </div>
    </div>

    <div v-if="!gameOver" class="draggable-items">
      <div
        v-for="item in draggableItems"
        :key="item.id"
        class="draggable-emoji"
        :style="draggedItem === item ? draggedStyle : {}"
        draggable="true"
        @dragstart="(e) => handleDragStart(e, item)"
        @click="speakOutLoud(item.name)"
        @touchstart="(e) => handleTouchStart(e, item)"
        @touchmove="handleTouchMove"
        @touchend="(e) => handleTouchEnd(e, targetRef)"
      >
        {{ item.emoji }}
      </div>
    </div>

    <div
      v-if="isTouchDragging && draggedItem"
      class="floating-emoji"
      :style="{ top: touchPosition.y + 'px', left: touchPosition.x + 'px' }"
    >
      {{ draggedItem.emoji }}
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from "vue";
import LevelComplete from "../LevelComplete.vue";
import { useDragAndDrop } from "@/composables/useDragAndDrop";
import { useSpeechSynthesis } from "@/composables/useSpeechSynthesis";

export default {
  components: { LevelComplete },
  setup() {
    const professionsList = [
      {
        name: "Doctor",
        backgroundImage:
          "https://img.freepik.com/free-vector/female-doctor-empty-speech-bubble_74855-6458.jpg",
      },
      {
        name: "Chef",
        backgroundImage:
          "https://img.freepik.com/free-vector/illustration-restaurant-vector-set_53876-44076.jpg",
      },
      {
        name: "Engineer",
        backgroundImage:
          "https://img.freepik.com/free-vector/engineering-construction_23-2148880432.jpg",
      },
      {
        name: "Artist",
        backgroundImage:
          "https://img.freepik.com/free-vector/modern-people-doing-cultural-activities_23-2148596544.jpg",
      },
      {
        name: "Teacher",
        backgroundImage:
          "https://img.freepik.com/free-vector/teaching-english-class-vector-new-normal-character-flat-graphic_53876-105802.jpg",
      },
    ];

    const professionItems = [
      { id: 1, emoji: "🩺", profession: "Doctor", name: "Stethoscope" },
      { id: 2, emoji: "💊", profession: "Doctor", name: "Medicine" },
      { id: 3, emoji: "👩‍🍳", profession: "Chef", name: "Chef" },
      { id: 4, emoji: "🍳", profession: "Chef", name: "Cooking Pan" },
      { id: 5, emoji: "🔧", profession: "Engineer", name: "Wrench" },
      { id: 6, emoji: "⚙️", profession: "Engineer", name: "Gear" },
      { id: 7, emoji: "🎨", profession: "Artist", name: "Paint Palette" },
      { id: 8, emoji: "🖌️", profession: "Artist", name: "Paint Brush" },
      { id: 9, emoji: "📚", profession: "Teacher", name: "Books" },
      { id: 10, emoji: "🖍️", profession: "Teacher", name: "Crayons" },
    ];

    const currentProfessionIndex = ref(0);
    const gameOver = ref(false);
    const correctDrops = ref({});
    const totalItemsPerProfession = {};
    const draggableItems = ref([]);
    const draggedItem = ref(null);
    const draggedStyle = ref({});
    const targetRef = ref(null);

    const isTouchDragging = ref(false);
    const touchPosition = ref({ x: 0, y: 0 });

    const { speakOutLoud } = useSpeechSynthesis();

    const currentProfession = computed(() =>
      gameOver.value ? null : professionsList[currentProfessionIndex.value]
    );

    const playSound = (url) => new Audio(url).play();

    const setupDraggableItems = () => {
      if (!currentProfession.value) return;

      const correct = professionItems.filter(
        (item) => item.profession === currentProfession.value.name
      );

      const incorrect = professionItems
        .filter((item) => item.profession !== currentProfession.value.name)
        .sort(() => 0.5 - Math.random())
        .slice(0, 1); // Only one incorrect

      const total = [...correct, ...incorrect].sort(() => 0.5 - Math.random());
      draggableItems.value = total.slice(0, 3); // Show only 3 at a time
    };

    professionsList.forEach((p) => {
      correctDrops.value[p.name] = [];
      totalItemsPerProfession[p.name] = professionItems.filter(
        (i) => i.profession === p.name
      ).length;
    });

    const {
      handleDragStart,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      handleDrop: internalHandleDrop,
    } = useDragAndDrop({
      onDragStart: (item) => {
        draggedItem.value = item;
      },
      onDragEnd: () => {
        draggedItem.value = null;
        draggedStyle.value = {};
        isTouchDragging.value = false;
      },
      onDrop: ({ data, valid }) => {
        if (!valid || !currentProfession.value) return;

        const targetProfession = currentProfession.value.name;

        if (
          !correctDrops.value[targetProfession].some((i) => i.id === data.id)
        ) {
          correctDrops.value[targetProfession].push(data);
          draggableItems.value = draggableItems.value.filter(
            (i) => i.id !== data.id
          );

          speakOutLoud(data.name);

          if (
            correctDrops.value[targetProfession].length ===
            totalItemsPerProfession[targetProfession]
          ) {
            playSound(
              "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
            );

            setTimeout(() => {
              currentProfessionIndex.value++;
              if (currentProfessionIndex.value >= professionsList.length) {
                gameOver.value = true;
                setTimeout(() => {
                  window.location.href = "/professions";
                }, 4000);
                speakOutLoud("Great job!");
              } else {
                setupDraggableItems();
              }
            }, 2000);
          }
        }
      },
      isValidDropTarget: (data) =>
        currentProfession.value &&
        data.profession === currentProfession.value.name,
    });

    const handleDrop = (event) => {
      if (!currentProfession.value) return;

      try {
        const data = JSON.parse(event.dataTransfer.getData("text/plain"));
        const valid = data.profession === currentProfession.value.name;

        if (valid) {
          internalHandleDrop(event, targetRef.value);
        } else {
          speakOutLoud("Try again");
        }
      } catch (err) {
        console.error("Invalid drag data", err);
      }
    };

    const handleTouchStartOverride = (e, item) => {
      isTouchDragging.value = true;
      touchPosition.value = {
        x: e.touches[0].clientX - 25,
        y: e.touches[0].clientY - 25,
      };
      handleTouchStart(e, item);
    };

    const handleTouchMoveOverride = (e) => {
      if (!isTouchDragging.value) return;
      touchPosition.value = {
        x: e.touches[0].clientX - 25,
        y: e.touches[0].clientY - 25,
      };
      handleTouchMove(e);
    };

    const handleTouchEndOverride = (e, targetRef) => {
      isTouchDragging.value = false;
      handleTouchEnd(e, targetRef);
    };

    onMounted(() => {
      setupDraggableItems();
      if (currentProfession.value) {
        speakOutLoud(currentProfession.value.name);
      }
    });

    watch(currentProfession, (newVal, oldVal) => {
      if (newVal && newVal.name !== oldVal?.name) {
        speakOutLoud(newVal.name);
      }
    });

    return {
      currentProfession,
      draggableItems,
      handleDragStart,
      handleTouchStart: handleTouchStartOverride,
      handleTouchMove: handleTouchMoveOverride,
      handleTouchEnd: handleTouchEndOverride,
      handleDrop,
      draggedItem,
      draggedStyle,
      correctDrops,
      targetRef,
      speakOutLoud,
      gameOver,
      isTouchDragging,
      touchPosition,
    };
  },
};
</script>

<style scoped>
.profession-matching-game {
  position: relative;
  max-width: 500px;
  width: 80%;
  margin: auto;
}

.profession-target {
  position: relative;
  height: 300px;
  background-size: cover;
  background-position: center;
  border-radius: 10px;
  cursor: pointer;
  overflow: hidden;
}

.profession-label {
  margin: 10px auto;
  background-color: white;
  padding: 20px;
  border-radius: 10px;
  max-width: 150px;
}

.draggable-emoji {
  font-size: 5rem;
  display: inline-block;
  margin: 10px;
  cursor: move;
}

.dropped-items {
  position: absolute;
  bottom: 0;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.6);
  text-align: center;
  padding: 5px 0;
  font-size: 3rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.floating-emoji {
  position: fixed;
  font-size: 5rem;
  pointer-events: none;
  z-index: 9999;
  transform: translate(-50%, -50%);
}
</style>
